export const USER_ID_PATH_TOKEN = "<userId>";
export const USER_DATA_PATH_ROOT = `/data/${USER_ID_PATH_TOKEN}`;
export const SYSTEM_DATA_PATH_ROOT = "/data/system";
export const DEFAULT_REPORT_CACHE_FILE_NAME = "report";
export const DEFAULT_USER_REPORT_CACHE_PATH =
  `${USER_DATA_PATH_ROOT}/cache/${DEFAULT_REPORT_CACHE_FILE_NAME}`;
export const DEFAULT_SYSTEM_REPORT_CACHE_PATH =
  `${SYSTEM_DATA_PATH_ROOT}/cache/${DEFAULT_REPORT_CACHE_FILE_NAME}`;

export const AUTHORING_PATH_FIELD_MODES = [
  "existing-file",
  "existing-folder",
  "output-target",
  "report-cache",
] as const;

export type AuthoringPathFieldMode =
  (typeof AUTHORING_PATH_FIELD_MODES)[number];

export type AuthoringPathKind =
  | "empty"
  | "absolute"
  | "relative"
  | "url";

export type AuthoringPathScope =
  | "site"
  | "user"
  | "system"
  | "external"
  | "unknown";

export type AuthoringPathWarningCode =
  | "legacy-relative"
  | "concrete-user-path"
  | "external-url-not-allowed";

export interface AuthoringPathWarning {
  code: AuthoringPathWarningCode;
  message: string;
}

export interface AuthoringPathClassification {
  value: string;
  kind: AuthoringPathKind;
  scope: AuthoringPathScope;
  isLegacy: boolean;
  isHttpUrl: boolean;
  hasUserIdToken: boolean;
  hasTemplate: boolean;
  warning: AuthoringPathWarning | null;
}

export interface AuthoringPathFieldModeDefinition {
  selection: "file" | "folder";
  preserveFilename: boolean;
  pairedScopes: boolean;
}

export interface AuthoringPathScopeSwitchResult {
  value: string;
  changed: boolean;
  sourceScope: AuthoringPathScope;
  targetScope: "site" | "user" | "system";
  reason?: "external-path" | "unknown-user-path" | "unsafe-relative-segment";
}

export interface AuthoringPathParts {
  folder: string;
  filename: string;
}

export interface ReportCachePathPair {
  user: string;
  system: string;
}

export interface FileApiPathOptions {
  userId?: string | null;
  label?: string;
  allowHttpUrls?: boolean;
  resourceDefinitionPath?: string | null;
}

export const AUTHORING_PATH_FIELD_MODE_DEFINITIONS: Readonly<
  Record<AuthoringPathFieldMode, AuthoringPathFieldModeDefinition>
> = {
  "existing-file": {
    selection: "file",
    preserveFilename: false,
    pairedScopes: false,
  },
  "existing-folder": {
    selection: "folder",
    preserveFilename: false,
    pairedScopes: false,
  },
  "output-target": {
    selection: "folder",
    preserveFilename: true,
    pairedScopes: false,
  },
  "report-cache": {
    selection: "folder",
    preserveFilename: true,
    pairedScopes: true,
  },
};

const HTTP_URL_PATTERN = /^https?:\/\//i;
const TEMPLATE_PATTERN = /\{\{[\s\S]*?\}\}/;
const DATA_OWNER_PATTERN = /^\/?data\/([^/]+)(?:\/|$)/;

const LEGACY_RELATIVE_WARNING: AuthoringPathWarning = {
  code: "legacy-relative",
  message:
    "This legacy relative path remains supported. Select or enter an absolute site path beginning with / when updating it.",
};

const CONCRETE_USER_PATH_WARNING: AuthoringPathWarning = {
  code: "concrete-user-path",
  message:
    "This path contains a concrete or unrecognized data owner. Use /data/<userId>/... for the current candidate or /data/system/... for shared data.",
};

const EXTERNAL_URL_WARNING: AuthoringPathWarning = {
  code: "external-url-not-allowed",
  message: "This field requires a site file path and does not accept HTTP(S) URLs.",
};

function trimPath(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function withoutTrailingSlashes(value: string): string {
  if (value === "/") return value;
  return value.replace(/\/+$/, "");
}

function dataOwner(value: string): string | null {
  return DATA_OWNER_PATTERN.exec(value)?.[1] ?? null;
}

function classifyScope(value: string, isHttpUrl: boolean): AuthoringPathScope {
  if (!value) return "unknown";
  if (isHttpUrl) return "external";

  const owner = dataOwner(value);
  if (owner === USER_ID_PATH_TOKEN) return "user";
  if (owner === "system") return "system";
  if (owner) return "unknown";
  return "site";
}

export function isHttpPathUrl(
  value: string | null | undefined,
): boolean {
  return HTTP_URL_PATTERN.test(trimPath(value));
}

export function classifyAuthoringPath(
  value: string | null | undefined,
  options: {
    allowHttpUrls?: boolean;
    resourceDefinitionPath?: string | null;
  } = {},
): AuthoringPathClassification {
  const normalized = trimPath(value);
  const isHttpUrl = isHttpPathUrl(normalized);
  const kind: AuthoringPathKind = !normalized
    ? "empty"
    : isHttpUrl
      ? "url"
      : normalized.startsWith("/")
        ? "absolute"
        : "relative";
  const scope = classifyScope(normalized, isHttpUrl);
  const hasUserIdToken = normalized.includes(USER_ID_PATH_TOKEN);
  const hasTemplate = TEMPLATE_PATTERN.test(normalized);

  let warning: AuthoringPathWarning | null = null;
  if (isHttpUrl && !options.allowHttpUrls) {
    warning = EXTERNAL_URL_WARNING;
  } else if (scope === "unknown" && kind !== "empty") {
    warning = CONCRETE_USER_PATH_WARNING;
  } else if (kind === "relative" && !trimPath(options.resourceDefinitionPath)) {
    warning = LEGACY_RELATIVE_WARNING;
  }

  return {
    value: normalized,
    kind,
    scope,
    isLegacy:
      (kind === "relative" && !trimPath(options.resourceDefinitionPath))
      || (scope === "unknown" && kind !== "empty"),
    isHttpUrl,
    hasUserIdToken,
    hasTemplate,
    warning,
  };
}

export function toAbsoluteSitePath(
  value: string | null | undefined,
): string {
  const normalized = trimPath(value);
  if (!normalized || isHttpPathUrl(normalized)) return normalized;
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
}

export function toFileApiPath(
  value: string | null | undefined,
): string {
  const normalized = trimPath(value);
  if (isHttpPathUrl(normalized)) return normalized;
  return normalized.replace(/^\/+/, "");
}

function splitPathSuffix(value: string): {
  pathname: string;
  suffix: string;
} {
  const suffixIndex = value.search(/[?#]/);
  if (suffixIndex < 0) {
    return { pathname: value, suffix: "" };
  }
  return {
    pathname: value.slice(0, suffixIndex),
    suffix: value.slice(suffixIndex),
  };
}

function normalizedAbsoluteSegments(value: string): string[] {
  return toAbsoluteSitePath(value)
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);
}

function resolveRelativeSegments(
  baseSegments: readonly string[],
  relativePath: string,
): string[] {
  const resolved = [...baseSegments];
  for (const segment of relativePath.split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") {
      if (!resolved.length) {
        throw new Error("Relative resource path cannot traverse above the site root");
      }
      resolved.pop();
      continue;
    }
    resolved.push(segment);
  }
  return resolved;
}

export function resourceDefinitionBasePath(
  resourceDefinitionPath: string | null | undefined,
): string {
  const normalized = toAbsoluteSitePath(resourceDefinitionPath);
  if (!normalized || isHttpPathUrl(normalized)) return "";

  const { pathname } = splitPathSuffix(normalized);
  const withoutTrailingSlash = withoutTrailingSlashes(pathname);
  if (!withoutTrailingSlash || withoutTrailingSlash === "/") return "/";

  return authoringPathBasename(withoutTrailingSlash) === "index"
    ? authoringPathDirname(withoutTrailingSlash) || "/"
    : withoutTrailingSlash;
}

export function resolveResourcePath(
  value: string | null | undefined,
  resourceDefinitionPath: string | null | undefined,
): string {
  const normalized = trimPath(value);
  if (
    !normalized
    || isHttpPathUrl(normalized)
    || normalized.startsWith("/")
  ) {
    return normalized;
  }

  const basePath = resourceDefinitionBasePath(resourceDefinitionPath);
  if (!basePath) return normalized;

  const { pathname, suffix } = splitPathSuffix(normalized);
  const resolvedSegments = resolveRelativeSegments(
    normalizedAbsoluteSegments(basePath),
    pathname,
  );
  return `/${resolvedSegments.join("/")}${suffix}`;
}

export function toResourceRelativePath(
  selectedPath: string | null | undefined,
  resourceDefinitionPath: string | null | undefined,
): string | null {
  const selected = trimPath(selectedPath);
  const basePath = resourceDefinitionBasePath(resourceDefinitionPath);
  if (
    !selected
    || !basePath
    || isHttpPathUrl(selected)
    || isHttpPathUrl(basePath)
  ) {
    return null;
  }

  const { pathname, suffix } = splitPathSuffix(toAbsoluteSitePath(selected));
  const baseSegments = normalizedAbsoluteSegments(basePath);
  const selectedSegments = normalizedAbsoluteSegments(pathname);
  let commonLength = 0;
  while (
    commonLength < baseSegments.length
    && commonLength < selectedSegments.length
    && baseSegments[commonLength] === selectedSegments[commonLength]
  ) {
    commonLength++;
  }

  const upward = baseSegments.length - commonLength;
  const downward = selectedSegments.slice(commonLength);
  const relativeSegments = [
    ...Array.from({ length: upward }, () => ".."),
    ...downward,
  ];
  return `${relativeSegments.join("/") || "."}${suffix}`;
}

export function isResourcePathDescendant(
  selectedPath: string | null | undefined,
  resourceDefinitionPath: string | null | undefined,
): boolean {
  const selected = toAbsoluteSitePath(selectedPath);
  const basePath = resourceDefinitionBasePath(resourceDefinitionPath);
  if (!selected || !basePath || isHttpPathUrl(selected)) return false;
  if (basePath === "/") return selected !== "/" && selected.startsWith("/");
  return selected.startsWith(`${basePath}/`);
}

export function toStoredResourceSelectionPath(
  selectedPath: string | null | undefined,
  resourceDefinitionPath: string | null | undefined,
  saveAsRelative?: boolean,
): string {
  const absolute = toAbsoluteSitePath(selectedPath);
  if (!absolute || isHttpPathUrl(absolute)) return absolute;

  const shouldSaveAsRelative = saveAsRelative
    ?? isResourcePathDescendant(absolute, resourceDefinitionPath);
  if (shouldSaveAsRelative) {
    return toResourceRelativePath(absolute, resourceDefinitionPath) ?? absolute;
  }
  return absolute;
}

export function resolveSiteFileDownloadUrl(
  value: string | null | undefined,
  apiUrl: string | null | undefined,
  siteId: string | null | undefined,
  resourceDefinitionPath?: string | null,
): string {
  const normalized = resolveResourcePath(value, resourceDefinitionPath);
  if (!normalized || isHttpPathUrl(normalized)) return normalized;

  const normalizedSiteId = trimPath(siteId);
  if (!normalizedSiteId) return "";

  const normalizedApiUrl = trimPath(apiUrl).replace(/\/+$/, "");
  return `${normalizedApiUrl}/v1/file/${encodeURIComponent(normalizedSiteId)}/download/${toFileApiPath(normalized)}`;
}

export function resolveFileApiPath(
  value: string | null | undefined,
  options: FileApiPathOptions = {},
): string {
  const normalized = resolveResourcePath(
    value,
    options.resourceDefinitionPath,
  );
  const label = options.label?.trim() || "File path";

  if (!normalized) return "";
  if (isHttpPathUrl(normalized)) {
    if (options.allowHttpUrls) return normalized;
    throw new Error(`${label} does not accept HTTP(S) URLs`);
  }

  if (normalized.includes(USER_ID_PATH_TOKEN) && !trimPath(options.userId)) {
    throw new Error(
      `${label} requires a candidate userId to resolve ${USER_DATA_PATH_ROOT}/...`,
    );
  }

  return toFileApiPath(resolveUserPath(normalized, options.userId));
}

function assertUserId(userId: string | null | undefined): string {
  const normalized = trimPath(userId);
  if (
    !normalized
    || normalized.includes("/")
    || normalized.includes("\\")
    || normalized.includes("?")
    || normalized.includes("#")
  ) {
    throw new Error("A concrete userId path segment is required");
  }
  return normalized;
}

export function resolveUserPath(
  value: string | null | undefined,
  userId: string | null | undefined,
): string {
  const normalized = trimPath(value);
  if (!normalized.includes(USER_ID_PATH_TOKEN)) return normalized;
  return normalized.replaceAll(USER_ID_PATH_TOKEN, assertUserId(userId));
}

export function retokenizeUserPath(
  value: string | null | undefined,
  userId: string | null | undefined,
): string {
  const normalized = trimPath(value);
  if (!normalized) return normalized;

  const leadingSlash = normalized.startsWith("/") ? "/" : "";
  const segments = normalized.replace(/^\/+/, "").split("/");

  if (
    segments[0] !== "data"
    || !segments[1]
    || segments[1] === "system"
    || segments[1] === USER_ID_PATH_TOKEN
  ) {
    return normalized;
  }

  const concreteUserId = assertUserId(userId);
  if (segments[1] !== concreteUserId) return normalized;

  segments[1] = USER_ID_PATH_TOKEN;
  return `${leadingSlash}${segments.join("/")}`;
}

function scopeRoot(scope: "site" | "user" | "system"): string {
  if (scope === "user") return USER_DATA_PATH_ROOT;
  if (scope === "system") return SYSTEM_DATA_PATH_ROOT;
  return "/";
}

function scopeSuffix(
  value: string,
  scope: "site" | "user" | "system",
): string {
  const absolute = toAbsoluteSitePath(value);
  if (scope === "user") return absolute.slice(USER_DATA_PATH_ROOT.length);
  if (scope === "system") return absolute.slice(SYSTEM_DATA_PATH_ROOT.length);
  return absolute;
}

function hasUnsafeRelativeSegment(value: string): boolean {
  return value.split("/").some((segment) => segment === "." || segment === "..");
}

export function switchAuthoringPathScope(
  value: string | null | undefined,
  targetScope: "site" | "user" | "system",
): AuthoringPathScopeSwitchResult {
  const classification = classifyAuthoringPath(value, { allowHttpUrls: true });
  const normalized = classification.value;

  if (classification.scope === "external") {
    return {
      value: normalized,
      changed: false,
      sourceScope: classification.scope,
      targetScope,
      reason: "external-path",
    };
  }

  if (classification.scope === "unknown" && classification.kind !== "empty") {
    return {
      value: normalized,
      changed: false,
      sourceScope: classification.scope,
      targetScope,
      reason: "unknown-user-path",
    };
  }

  const sourceScope = classification.kind === "empty"
    ? "site"
    : classification.scope as "site" | "user" | "system";
  const suffix = classification.kind === "empty"
    ? ""
    : scopeSuffix(normalized, sourceScope);

  if (hasUnsafeRelativeSegment(suffix)) {
    return {
      value: normalized,
      changed: false,
      sourceScope: classification.scope,
      targetScope,
      reason: "unsafe-relative-segment",
    };
  }

  const cleanSuffix = suffix.replace(/^\/+|\/+$/g, "");
  const root = scopeRoot(targetScope);
  const switched = targetScope === "site"
    ? cleanSuffix ? `/${cleanSuffix}` : "/"
    : cleanSuffix ? `${root}/${cleanSuffix}` : root;

  return {
    value: switched,
    changed: switched !== normalized,
    sourceScope: classification.scope,
    targetScope,
  };
}

export function splitAuthoringPath(
  value: string | null | undefined,
): AuthoringPathParts {
  const normalized = withoutTrailingSlashes(trimPath(value));
  if (!normalized || normalized === "/") {
    return { folder: normalized, filename: "" };
  }

  const separator = normalized.lastIndexOf("/");
  if (separator < 0) {
    return { folder: "", filename: normalized };
  }
  if (separator === 0) {
    return { folder: "/", filename: normalized.slice(1) };
  }

  return {
    folder: normalized.slice(0, separator),
    filename: normalized.slice(separator + 1),
  };
}

export function authoringPathBasename(
  value: string | null | undefined,
): string {
  return splitAuthoringPath(value).filename;
}

export function authoringPathDirname(
  value: string | null | undefined,
): string {
  return splitAuthoringPath(value).folder;
}

export function composeAuthoringPath(
  folder: string | null | undefined,
  filename: string | null | undefined,
): string {
  const normalizedFolder = withoutTrailingSlashes(trimPath(folder));
  const normalizedFilename = trimPath(filename).replace(/^\/+/, "");

  if (!normalizedFolder) return normalizedFilename;
  if (!normalizedFilename) return normalizedFolder;
  if (normalizedFolder === "/") return `/${normalizedFilename}`;
  return `${normalizedFolder}/${normalizedFilename}`;
}

export function deriveSystemReportCachePath(
  userPath: string | null | undefined,
): string | null {
  const switched = switchAuthoringPathScope(userPath, "system");
  return switched.sourceScope === "user" && !switched.reason
    ? switched.value
    : null;
}

export function deriveUserReportCachePath(
  systemPath: string | null | undefined,
): string | null {
  const switched = switchAuthoringPathScope(systemPath, "user");
  return switched.sourceScope === "system" && !switched.reason
    ? switched.value
    : null;
}

export function buildReportCachePathPair(
  filename: string | null | undefined = DEFAULT_REPORT_CACHE_FILE_NAME,
): ReportCachePathPair {
  const normalizedFilename =
    authoringPathBasename(filename) || DEFAULT_REPORT_CACHE_FILE_NAME;
  return {
    user: composeAuthoringPath(
      `${USER_DATA_PATH_ROOT}/cache`,
      normalizedFilename,
    ),
    system: composeAuthoringPath(
      `${SYSTEM_DATA_PATH_ROOT}/cache`,
      normalizedFilename,
    ),
  };
}

export function normalizeCandidateReportCachePath(
  value: string | null | undefined,
): string {
  const normalized = trimPath(value);
  if (!normalized) return DEFAULT_USER_REPORT_CACHE_PATH;
  if (isHttpPathUrl(normalized)) {
    throw new Error("Candidate report cache path does not accept HTTP(S) URLs");
  }

  return normalized.includes("/")
    ? toAbsoluteSitePath(normalized)
    : composeAuthoringPath(`${USER_DATA_PATH_ROOT}/cache`, normalized);
}

export function resolveCandidateReportCachePath(
  value: string | null | undefined,
  userId: string | null | undefined,
): string {
  return resolveFileApiPath(normalizeCandidateReportCachePath(value), {
    userId,
    label: "Candidate report cache path",
  });
}

export function resolveSystemReportCachePath(
  candidatePath: string | null | undefined,
): string {
  const normalized = normalizeCandidateReportCachePath(candidatePath);
  const systemPath = deriveSystemReportCachePath(normalized);
  if (!systemPath) {
    throw new Error(
      "Candidate report cache path must use /data/<userId>/... so the system aggregate path can be derived",
    );
  }
  return toFileApiPath(systemPath);
}

export function resolveCandidateScopedFileApiPath(
  containerPath: string | null | undefined,
  childPath: string | null | undefined,
  userId: string | null | undefined,
  label = "Candidate file path",
): string {
  const combined = composeAuthoringPath(containerPath, childPath);
  const classification = classifyAuthoringPath(combined, {
    allowHttpUrls: false,
  });

  if (classification.isHttpUrl) {
    throw new Error(`${label} does not accept HTTP(S) URLs`);
  }

  if (
    classification.scope === "user"
    || classification.scope === "system"
    || classification.scope === "unknown"
  ) {
    return resolveFileApiPath(combined, { userId, label });
  }

  if (!trimPath(userId)) {
    throw new Error(
      `${label} requires a candidate userId because this legacy path is candidate-scoped`,
    );
  }
  const concreteUserId = assertUserId(userId);
  const suffix = toFileApiPath(combined);
  return suffix
    ? `data/${concreteUserId}/${suffix}`
    : `data/${concreteUserId}`;
}
